import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { getCourseById, updateCourse, apiUrl } from "../../../api";
import {
  VALID_LINK,
  ALPHA_NUMERIC_SPACE,
  testRegex,
} from "../../../config/regex";
import Button from "../../../components/Button";
import TextInput from "../../../components/TextInput";
import Upload from "../../../components/Upload";
import Dialog from "../../../components/Dialog";
import { Tooltip } from "@mui/material";
import { confirmDialog } from "../../../utils/confirm";
import { Play, Pencil, Trash2, HelpCircle } from "lucide-react";
import PageLoader from "../../../components/PageLoader";
import Swal from "sweetalert2";

/** Stable id for lesson rows (API / legacy docs may omit `id`) */
const normalizeLessons = (lessons) =>
  (lessons || []).map((l) => ({
    ...l,
    id:
      l.id != null && String(l.id) !== ""
        ? String(l.id)
        : l._id != null
          ? String(l._id)
          : typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `lesson-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  }));

const normalizeQuiz = (quiz) =>
  (quiz || []).map((q) => ({
    ...q,
    id:
      q.id != null && String(q.id) !== ""
        ? String(q.id)
        : q._id != null
          ? String(q._id)
          : typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `quiz-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  }));

const isValidHttpUrl = (value) => {
  if (!value || typeof value !== "string") return false;
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch (_) {
    return false;
  }
};

const emptyQuizForm = () => ({
  question: "",
  option0: "",
  option1: "",
  option2: "",
  option3: "",
  correct: 0,
});

const CourseDetail = () => {
  const { courseId } = useParams();
  const lessonFormRef = useRef(null);
  const quizFormRef = useRef(null);
  const [course, setCourse] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [lessonForm, setLessonForm] = useState({
    title: "",
    shortDescription: "",
    videoUrl: "",
    videoFileName: "",
  });
  const [initialLessonForm, setInitialLessonForm] = useState({
    title: "",
    shortDescription: "",
    videoUrl: "",
    videoFileName: "",
  });
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [showLessonForm, setShowLessonForm] = useState(false);
  const [previewVideoUrl, setPreviewVideoUrl] = useState(null);
  const [expandedDescId, setExpandedDescId] = useState(null);
  const [quizForm, setQuizForm] = useState(emptyQuizForm());
  const [initialQuizForm, setInitialQuizForm] = useState(emptyQuizForm());
  const [editingQuizId, setEditingQuizId] = useState(null);
  const [showQuizForm, setShowQuizForm] = useState(false);

  const [lessonErrors, setLessonErrors] = useState({});
  const [lessonTouched, setLessonTouched] = useState({});
  const [quizErrors, setQuizErrors] = useState({});
  const [quizTouched, setQuizTouched] = useState({});

  const isQuizInvalid = React.useMemo(() => {
    const hasErrors =
      Object.keys(quizErrors).filter((k) => k !== "submit").length > 0;
    const hasEmptyFields =
      !String(quizForm.question || "").trim() ||
      !String(quizForm.option0 || "").trim() ||
      !String(quizForm.option1 || "").trim() ||
      !String(quizForm.option2 || "").trim() ||
      !String(quizForm.option3 || "").trim();
    return hasErrors || hasEmptyFields;
  }, [quizErrors, quizForm]);

  const isLessonInvalid = React.useMemo(() => {
    const hasErrors =
      Object.keys(lessonErrors).filter((k) => k !== "submit").length > 0;
    const hasEmptyFields =
      !String(lessonForm.title || "").trim() ||
      !String(lessonForm.shortDescription || "").trim() ||
      !String(lessonForm.videoUrl || "").trim();
    return hasErrors || hasEmptyFields;
  }, [lessonErrors, lessonForm]);

  const isQuizChanged = React.useMemo(() => {
    return JSON.stringify(quizForm) !== JSON.stringify(initialQuizForm);
  }, [quizForm, initialQuizForm]);

  const isLessonChanged = React.useMemo(() => {
    return JSON.stringify(lessonForm) !== JSON.stringify(initialLessonForm);
  }, [lessonForm, initialLessonForm]);

  const SHORT_DESC_LENGTH = 60;
  const MAX_QUIZ_QUESTIONS = 20;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const c = await getCourseById(courseId);
        if (cancelled) return;
        if (!c) {
          setCourse(null);
          return;
        }
        setCourse({
          ...c,
          lessons: normalizeLessons(c.lessons),
          quiz: normalizeQuiz(c.quiz),
        });
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err.response?.data?.message ||
              err.message ||
              "Failed to load course",
          );
          setCourse(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  const persistCourse = useCallback(
    async (updates) => {
      if (!courseId) return null;
      setSaving(true);
      setSaveError("");
      const payload = { ...updates };
      if (payload.lessons) {
        payload.lessons = payload.lessons.map((l) => ({
          id: l.id,
          title: l.title,
          shortDescription: l.shortDescription,
          videoUrl: l.videoUrl,
          videoFileName: l.videoFileName,
        }));
      }
      if (payload.quiz) {
        payload.quiz = payload.quiz.map((q) => ({
          id: q.id,
          question: q.question,
          options: q.options,
          correct: q.correct,
        }));
      }
      try {
        const updated = await updateCourse(courseId, payload);
        setCourse({
          ...updated,
          lessons: normalizeLessons(updated.lessons),
          quiz: normalizeQuiz(updated.quiz),
        });
        return updated;
      } catch (err) {
        setSaveError(
          err.response?.data?.message || err.message || "Failed to save",
        );
        return null;
      } finally {
        setSaving(false);
      }
    },
    [courseId],
  );

  const validateLesson = () => {
    const e = {};
    if (!String(lessonForm.title || "").trim()) {
      e.title = "Lesson title is required";
    } else if (!testRegex(ALPHA_NUMERIC_SPACE, lessonForm.title)) {
      e.title = "Title contains invalid characters";
    }

    if (!String(lessonForm.shortDescription || "").trim()) {
      e.shortDescription = "Short description is required";
    }

    if (!String(lessonForm.videoUrl || "").trim()) {
      e.videoUrl = "Lesson video is required";
    } else if (!testRegex(VALID_LINK, lessonForm.videoUrl)) {
      e.videoUrl = "Enter a valid video URL";
    }

    setLessonErrors((prev) => ({
      ...e,
      ...(prev.submit ? { submit: prev.submit } : {}),
    }));
    return Object.keys(e).length === 0;
  };

  const validateQuiz = () => {
    const e = {};
    if (!String(quizForm.question || "").trim()) {
      e.question = "Question is required";
    }

    if (!String(quizForm.option0 || "").trim()) {
      e.option0 = "Option 1 is required";
    }
    if (!String(quizForm.option1 || "").trim()) {
      e.option1 = "Option 2 is required";
    }
    if (!String(quizForm.option2 || "").trim()) {
      e.option2 = "Option 3 is required";
    }
    if (!String(quizForm.option3 || "").trim()) {
      e.option3 = "Option 4 is required";
    }

    setQuizErrors((prev) => ({
      ...e,
      ...(prev.submit ? { submit: prev.submit } : {}),
    }));
    return Object.keys(e).length === 0;
  };

  useEffect(() => {
    validateLesson();
  }, [lessonForm.title, lessonForm.shortDescription, lessonForm.videoUrl]);

  useEffect(() => {
    validateQuiz();
  }, [
    quizForm.question,
    quizForm.option0,
    quizForm.option1,
    quizForm.option2,
    quizForm.option3,
  ]);

  const handleLessonBlur = (field) => {
    setLessonTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleQuizBlur = (field) => {
    setQuizTouched((prev) => ({ ...prev, [field]: true }));
  };

  const openAddLessonForm = () => {
    setEditingLessonId(null);
    const empty = {
      title: "",
      shortDescription: "",
      videoUrl: "",
      videoFileName: "",
    };
    setLessonForm(empty);
    setInitialLessonForm(empty);
    setLessonErrors({});
    setLessonTouched({});
    setShowLessonForm(true);
    setTimeout(() => {
      lessonFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const saveLesson = async () => {
    if (!validateLesson()) return;
    const videoUrl = (lessonForm.videoUrl || "").trim();

    const lessons = [...(course.lessons || [])];
    if (editingLessonId) {
      const li = lessons.findIndex((l) => l.id === editingLessonId);
      if (li !== -1) {
        const newUrl = videoUrl || lessons[li].videoUrl;
        lessons[li] = {
          ...lessons[li],
          title: lessonForm.title.trim(),
          shortDescription: (lessonForm.shortDescription || "").trim(),
          videoUrl: newUrl,
          videoFileName: lessonForm.videoFileName || lessons[li].videoFileName,
        };
      }
    } else {
      lessons.push({
        id: Date.now().toString(),
        title: lessonForm.title.trim(),
        shortDescription: (lessonForm.shortDescription || "").trim(),
        videoUrl,
        videoFileName: lessonForm.videoFileName || "",
      });
    }
    const ok = await persistCourse({ lessons });
    if (ok) {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: `Lesson ${editingLessonId ? "updated" : "added"} successfully`,
        confirmButtonColor: "#38915c",
      });
      setEditingLessonId(null);
      setLessonForm({
        title: "",
        shortDescription: "",
        videoUrl: "",
        videoFileName: "",
      });
      setShowLessonForm(false);
    }
  };

  const editLesson = (lesson) => {
    setEditingLessonId(lesson.id);
    const data = {
      title: lesson.title || "",
      shortDescription: lesson.shortDescription || "",
      videoUrl: lesson.videoUrl || "",
      videoFileName: lesson.videoFileName || "",
    };
    setLessonForm(data);
    setInitialLessonForm(data);
    setLessonErrors({});
    setLessonTouched({});
    setShowLessonForm(true);
    setTimeout(() => {
      lessonFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const deleteLesson = async (lessonId) => {
    if (!course) return;
    const confirmed = await confirmDialog({
      title: "Delete lesson?",
      text: "This lesson will be removed.",
    });
    if (!confirmed) return;
    const lessons = (course.lessons || []).filter((l) => l.id !== lessonId);
    const ok = await persistCourse({ lessons });
    if (ok) {
      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Lesson deleted successfully",
        confirmButtonColor: "#38915c",
      });
      if (editingLessonId === lessonId) {
        setEditingLessonId(null);
        setLessonForm({
          title: "",
          shortDescription: "",
          videoUrl: "",
          videoFileName: "",
        });
      }
    }
  };

  const cancelLessonForm = () => {
    setShowLessonForm(false);
    setEditingLessonId(null);
    setLessonForm({
      title: "",
      shortDescription: "",
      videoUrl: "",
      videoFileName: "",
    });
  };

  const moveLesson = async (lessonIndex, direction) => {
    if (!course) return;
    const lessons = [...(course.lessons || [])];
    const newIndex = direction === "up" ? lessonIndex - 1 : lessonIndex + 1;
    if (newIndex < 0 || newIndex >= lessons.length) return;
    [lessons[lessonIndex], lessons[newIndex]] = [
      lessons[newIndex],
      lessons[lessonIndex],
    ];
    await persistCourse({ lessons });
  };

  const openAddQuizForm = () => {
    setEditingQuizId(null);
    const empty = emptyQuizForm();
    setQuizForm(empty);
    setInitialQuizForm(empty);
    setQuizErrors({});
    setQuizTouched({});
    setShowQuizForm(true);
    setTimeout(() => {
      quizFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const saveQuizQuestion = async () => {
    if (!validateQuiz()) return;
    const options = [
      quizForm.option0,
      quizForm.option1,
      quizForm.option2,
      quizForm.option3,
    ]
      .map((o) => (o || "").trim())
      .filter(Boolean);
    if (options.length < 2) return;
    const correct = Math.min(3, Math.max(0, Number(quizForm.correct) || 0));
    const quiz = [...(course.quiz || [])];
    const payload = {
      id: editingQuizId || Date.now().toString(),
      question: quizForm.question.trim(),
      options,
      correct: Math.min(correct, options.length - 1),
    };
    if (editingQuizId) {
      const qi = quiz.findIndex((q) => q.id === editingQuizId);
      if (qi !== -1) quiz[qi] = payload;
    } else {
      if (quiz.length >= MAX_QUIZ_QUESTIONS) {
        setSaveError(
          `You can add up to ${MAX_QUIZ_QUESTIONS} quiz questions per course.`,
        );
        return;
      }
      quiz.push(payload);
    }
    const ok = await persistCourse({ quiz });
    if (ok) {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: `Quiz question ${editingQuizId ? "updated" : "added"} successfully`,
        confirmButtonColor: "#38915c",
      });
      setEditingQuizId(null);
      setQuizForm(emptyQuizForm());
      setShowQuizForm(false);
    }
  };

  const editQuizQuestion = (q) => {
    setEditingQuizId(q.id);
    const op = q.options || [];
    const data = {
      question: q.question || "",
      option0: op[0] || "",
      option1: op[1] || "",
      option2: op[2] || "",
      option3: op[3] || "",
      correct: typeof q.correct === "number" ? q.correct : 0,
    };
    setQuizForm(data);
    setInitialQuizForm(data);
    setQuizErrors({});
    setQuizTouched({});
    setShowQuizForm(true);
    setTimeout(() => {
      quizFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const deleteQuizQuestion = async (questionId) => {
    if (!course) return;
    const confirmed = await confirmDialog({
      title: "Delete this quiz question?",
      text: "It will be removed from the course quiz.",
    });
    if (!confirmed) return;
    const quiz = (course.quiz || []).filter((q) => q.id !== questionId);
    const ok = await persistCourse({ quiz });
    if (ok) {
      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Quiz question deleted successfully",
        confirmButtonColor: "#38915c",
      });
      if (editingQuizId === questionId) {
        setEditingQuizId(null);
        setQuizForm(emptyQuizForm());
        setInitialQuizForm(emptyQuizForm());
        setShowQuizForm(false);
      }
    }
  };

  const cancelQuizForm = () => {
    setShowQuizForm(false);
    setEditingQuizId(null);
    const empty = emptyQuizForm();
    setQuizForm(empty);
    setInitialQuizForm(empty);
  };

  const moveQuizQuestion = async (questionIndex, direction) => {
    if (!course) return;
    const quiz = [...(course.quiz || [])];
    const newIndex = direction === "up" ? questionIndex - 1 : questionIndex + 1;
    if (newIndex < 0 || newIndex >= quiz.length) return;
    [quiz[questionIndex], quiz[newIndex]] = [
      quiz[newIndex],
      quiz[questionIndex],
    ];
    await persistCourse({ quiz });
  };

  const isVideoLink = (url) =>
    url && (url.startsWith("http://") || url.startsWith("https://"));
  const isVideoUpload = (url) => url && url.startsWith("data:");

  /** Returns embed URL for YouTube/Vimeo, or null for direct video / data URLs */
  const getVideoEmbedUrl = (url) => {
    if (!url || typeof url !== "string") return null;
    try {
      if (url.includes("youtube.com/watch") || url.includes("youtu.be/")) {
        const id = url.includes("youtu.be/")
          ? url.split("youtu.be/")[1]?.split("?")[0]
          : new URL(url).searchParams.get("v");
        return id ? `https://www.youtube.com/embed/${id}` : null;
      }
      if (url.includes("vimeo.com/") && !url.includes("/video/")) {
        const id = url.split("vimeo.com/")[1]?.split("?")[0]?.split("/")[0];
        return id ? `https://player.vimeo.com/video/${id}` : null;
      }
      if (url.includes("player.vimeo.com/video/")) return url;
    } catch (_) {}
    return null;
  };

  /** YouTube/Cloudinary thumbnail for lesson preview. */
  const getLessonThumbnailUrl = (videoUrl) => {
    if (!videoUrl || typeof videoUrl !== "string") return null;
    const u = videoUrl.trim();
    const watch = u.match(/[?&]v=([\w-]{11})/);
    if (watch) return `https://img.youtube.com/vi/${watch[1]}/mqdefault.jpg`;
    const short = u.match(/youtu\.be\/([\w-]{11})/);
    if (short) return `https://img.youtube.com/vi/${short[1]}/mqdefault.jpg`;
    if (u.includes("cloudinary.com") && u.includes("/video/upload/")) {
      return u
        .replace("/video/upload/", "/video/upload/c_fill,h_180,w_320/")
        .replace(/\.[^/.]+$/, ".jpg");
    }
    return null;
  };

  if (loading) {
    return <PageLoader />;
  }

  if (loadError || !course) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-gray-500">{loadError || "Course not found."}</p>
        <Link
          to="/admin/courses"
          className="mt-4 text-primary-600 hover:underline"
        >
          ← Back to Courses
        </Link>
      </div>
    );
  }

  const courseLessons = course.lessons || [];
  const courseQuiz = course.quiz || [];

  return (
    <div>
      <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-start gap-4 flex-wrap">
          <Link
            to="/admin/courses"
            className="text-gray-600 hover:text-primary-600 text-sm font-medium mt-1"
          >
            ← Courses
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{course.title}</h1>
            <p className="text-sm text-gray-600 mt-1">
              Type:{" "}
              <span className="font-medium text-gray-800">
                {course.courseType === "crypto" ? "Crypto" : "Indian"}
              </span>
              {" · "}
              Growth subscription plan:{" "}
              <span className="font-medium text-gray-800">
                {course.underGrowthPlan ? "Yes" : "No"}
              </span>
              <span className="text-gray-500"> — edit via Courses → Edit</span>
            </p>
          </div>
        </div>
      </div>

      {saveError && (
        <div
          className="mb-4 p-3 rounded-lg bg-danger-50 text-danger-600 text-sm"
          role="alert"
        >
          {saveError}
        </div>
      )}

      {/* Basic / intro video (1 per course) */}
      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6 border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">
          Basic / intro video (course-level)
        </h2>
        {course.basicVideoUrl ? (
          <div className="flex items-center gap-3 flex-wrap">
            <a
              href={course.basicVideoUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={course.basicVideoUrl}
              className="text-primary-600 hover:underline"
            >
              Open intro video
            </a>
            <Tooltip title="Play preview">
              <button
                type="button"
                onClick={() => setPreviewVideoUrl(course.basicVideoUrl)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-100 text-primary-700 text-sm font-medium hover:bg-primary-200 transition-colors"
              >
                <Play className="w-4 h-4" />
                Preview
              </button>
            </Tooltip>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No intro video added.</p>
        )}
      </div>

      {/* Lessons management */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex flex-wrap justify-between items-center gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Lessons</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {courseLessons.length} lesson
              {courseLessons.length !== 1 ? "s" : ""} • Reorder with ↑↓ or add
              one lesson at a time
            </p>
          </div>
          {!showLessonForm && (
            <Button
              variant="primary"
              size="sm"
              onClick={openAddLessonForm}
              disabled={saving}
            >
              Add lesson
            </Button>
          )}
        </div>

        {showLessonForm && (
          <div
            ref={lessonFormRef}
            className="p-6 bg-gray-50 border-b border-gray-200"
          >
            <h3 className="font-medium text-gray-800 mb-3">
              {editingLessonId ? "Edit Lesson" : "New Lesson"}
            </h3>
            <form
              id="lesson-form"
              onSubmit={(e) => {
                e.preventDefault();
                saveLesson();
              }}
              className="space-y-4 mb-4"
            >
              <TextInput
                name="lessonTitle"
                label="Lesson title"
                placeholder="e.g. Introduction to Stock Market"
                value={lessonForm.title}
                onChange={(e) => {
                  setLessonForm((f) => ({ ...f, title: e.target.value }));
                  setLessonTouched((t) => ({ ...t, title: true }));
                }}
                onBlur={() => handleLessonBlur("title")}
                error={lessonTouched.title ? lessonErrors.title : ""}
                required
              />
              <TextInput
                name="shortDescription"
                label="Short description"
                placeholder="Brief summary of what this lesson covers"
                value={lessonForm.shortDescription}
                onChange={(e) => {
                  setLessonForm((f) => ({
                    ...f,
                    shortDescription: e.target.value,
                  }));
                  setLessonTouched((t) => ({ ...t, shortDescription: true }));
                }}
                onBlur={() => handleLessonBlur("shortDescription")}
                multiline
                rows={2}
                error={
                  lessonTouched.shortDescription
                    ? lessonErrors.shortDescription
                    : ""
                }
                required
              />
              <Upload
                type="video"
                label="Lesson video"
                value={lessonForm.videoUrl}
                fileName={lessonForm.videoFileName}
                uploadEndpoint={apiUrl("/api/courses/upload-video")}
                onChange={(val, fname) => {
                  setLessonForm((f) => ({
                    ...f,
                    videoUrl: val,
                    videoFileName: fname || "",
                  }));
                  setLessonTouched((t) => ({ ...t, videoUrl: true }));
                }}
                onBlur={() => handleLessonBlur("videoUrl")}
                placeholder="https://... video URL"
                error={lessonTouched.videoUrl ? lessonErrors.videoUrl : ""}
                required
              />
            </form>
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                type="submit"
                form="lesson-form"
                disabled={saving || isLessonInvalid || !isLessonChanged}
              >
                {editingLessonId ? "Update Lesson" : "Add Lesson"}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={cancelLessonForm}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-700 w-12">
                  #
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700 w-24">
                  Thumbnail
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Title
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Short description
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Video
                </th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">
                  Order
                </th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {courseLessons.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">
                    No lessons yet. Click &quot;Add lesson&quot; to add one
                    lesson at a time.
                  </td>
                </tr>
              ) : (
                courseLessons.map((lesson, i) => (
                  <tr
                    key={lesson.id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-3 px-4 text-gray-600 font-medium">
                      {i + 1}
                    </td>
                    <td className="py-3 px-4">
                      {(() => {
                        const thumb = getLessonThumbnailUrl(lesson.videoUrl);
                        return thumb ? (
                          <img
                            src={thumb}
                            alt=""
                            className="w-16 h-10 object-cover rounded-md border border-gray-200 shadow-sm"
                          />
                        ) : (
                          <div className="w-16 h-10 bg-gray-100 rounded-md border border-gray-200 flex items-center justify-center">
                            <Play className="w-4 h-4 text-gray-400" />
                          </div>
                        );
                      })()}
                    </td>
                    <td className="py-3 px-4 font-medium">
                      {lesson.title || "—"}
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-sm max-w-[240px]">
                      {(() => {
                        const text = lesson.shortDescription || "—";
                        const isLong = text.length > SHORT_DESC_LENGTH;
                        const isExpanded = expandedDescId === lesson.id;
                        const showText =
                          isLong && !isExpanded
                            ? `${text.slice(0, SHORT_DESC_LENGTH)}...`
                            : text;
                        return (
                          <span>
                            {showText}
                            {isLong && (
                              <button
                                type="button"
                                onClick={() =>
                                  setExpandedDescId(
                                    isExpanded ? null : lesson.id,
                                  )
                                }
                                className="ml-1 text-primary-600 hover:text-primary-700 font-medium text-xs whitespace-nowrap"
                              >
                                {isExpanded ? "Read less" : "Read more"}
                              </button>
                            )}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="py-3 px-4">
                      {lesson.videoUrl ? (
                        isVideoLink(lesson.videoUrl) ? (
                          <a
                            href={lesson.videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:underline truncate block max-w-[200px]"
                          >
                            Link
                          </a>
                        ) : isVideoUpload(lesson.videoUrl) ? (
                          <span className="text-primary-600 truncate block max-w-[200px]">
                            Uploaded
                            {lesson.videoFileName
                              ? `: ${lesson.videoFileName}`
                              : ""}
                          </span>
                        ) : (
                          <span className="text-gray-600">Video</span>
                        )
                      ) : (
                        <span className="text-gray-400">Not set</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="inline-flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => moveLesson(i, "up")}
                          disabled={saving || i === 0}
                          className="p-1 rounded text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveLesson(i, "down")}
                          disabled={saving || i === courseLessons.length - 1}
                          className="p-1 rounded text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          ↓
                        </button>
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        {lesson.videoUrl && (
                          <Tooltip title="Play">
                            <button
                              type="button"
                              onClick={() =>
                                setPreviewVideoUrl(lesson.videoUrl)
                              }
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit">
                          <button
                            type="button"
                            onClick={() => editLesson(lesson)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <button
                            type="button"
                            onClick={() => deleteLesson(lesson.id)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-danger-600 hover:bg-danger-50 hover:text-danger-700 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Course Quiz management */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mt-6">
        <div className="p-4 border-b border-gray-200 flex flex-wrap justify-between items-center gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary-600" />
              Course Quiz
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {courseQuiz.length} question{courseQuiz.length !== 1 ? "s" : ""} •
              Enrolled users can take this quiz; others will be asked to
              buy/enrol
            </p>
          </div>
          {!showQuizForm && (
            <Button
              variant="primary"
              size="sm"
              onClick={openAddQuizForm}
              disabled={saving || courseQuiz.length >= MAX_QUIZ_QUESTIONS}
            >
              Add question
            </Button>
          )}
        </div>

        {showQuizForm && (
          <div
            ref={quizFormRef}
            className="p-6 bg-gray-50 border-b border-gray-200"
          >
            <h3 className="font-medium text-gray-800 mb-3">
              {editingQuizId ? "Edit Quiz Question" : "New Quiz Question"}
            </h3>
            <form
              id="quiz-form"
              onSubmit={(e) => {
                e.preventDefault();
                saveQuizQuestion();
              }}
              className="space-y-4 mb-4"
            >
              <TextInput
                name="quizQuestion"
                label="Question"
                placeholder="e.g. What is the full form of NSE?"
                value={quizForm.question}
                onChange={(e) => {
                  setQuizForm((f) => ({ ...f, question: e.target.value }));
                  setQuizTouched((t) => ({ ...t, question: true }));
                }}
                onBlur={() => handleQuizBlur("question")}
                error={quizTouched.question ? quizErrors.question : ""}
                required
              />
              {[0, 1, 2, 3].map((i) => (
                <TextInput
                  key={i}
                  name={`quizOption${i}`}
                  label={`Option ${i + 1}`}
                  placeholder={`Option ${i + 1}`}
                  value={quizForm[`option${i}`]}
                  onChange={(e) => {
                    setQuizForm((f) => ({
                      ...f,
                      [`option${i}`]: e.target.value,
                    }));
                    setQuizTouched((t) => ({ ...t, [`option${i}`]: true }));
                  }}
                  onBlur={() => handleQuizBlur(`option${i}`)}
                  error={
                    quizTouched[`option${i}`] ? quizErrors[`option${i}`] : ""
                  }
                  required={i < 4}
                />
              ))}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Correct answer (option number 1–4)
                </label>
                <select
                  value={quizForm.correct}
                  onChange={(e) =>
                    setQuizForm((f) => ({
                      ...f,
                      correct: Number(e.target.value),
                    }))
                  }
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {[0, 1, 2, 3].map((i) => (
                    <option key={i} value={i}>
                      Option {i + 1}
                    </option>
                  ))}
                </select>
              </div>
            </form>
            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                type="submit"
                form="quiz-form"
                disabled={saving || isQuizInvalid || !isQuizChanged}
              >
                {editingQuizId ? "Update Question" : "Add Question"}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={cancelQuizForm}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-gray-700 w-12">
                  #
                </th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">
                  Question
                </th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">
                  Order
                </th>
                <th className="text-right py-3 px-4 font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {courseQuiz.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-gray-500">
                    No quiz questions yet. Click &quot;Add question&quot; to add
                    questions for this course.
                  </td>
                </tr>
              ) : (
                courseQuiz.map((q, i) => (
                  <tr
                    key={q.id}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-3 px-4 text-gray-600 font-medium">
                      {i + 1}
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-800">
                      {q.question || "—"}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="inline-flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => moveQuizQuestion(i, "up")}
                          disabled={saving || i === 0}
                          className="p-1 rounded text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveQuizQuestion(i, "down")}
                          disabled={saving || i === courseQuiz.length - 1}
                          className="p-1 rounded text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          ↓
                        </button>
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <Tooltip title="Edit">
                          <button
                            type="button"
                            onClick={() => editQuizQuestion(q)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-primary-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <button
                            type="button"
                            onClick={() => deleteQuizQuestion(q.id)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-danger-600 hover:bg-danger-50 hover:text-danger-700 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog
        open={Boolean(previewVideoUrl)}
        onClose={() => setPreviewVideoUrl(null)}
        title="Video preview"
        size="lg"
        footer={null}
      >
        {previewVideoUrl &&
          (() => {
            const embedUrl = getVideoEmbedUrl(previewVideoUrl);
            if (embedUrl) {
              return (
                <div
                  className="relative w-full rounded-lg overflow-hidden bg-black"
                  style={{ paddingBottom: "56.25%" }}
                >
                  <iframe
                    src={embedUrl}
                    title="Video preview"
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              );
            }
            return (
              <video
                src={previewVideoUrl}
                controls
                className="w-full rounded-lg bg-black max-h-[70vh]"
                playsInline
              >
                Your browser does not support the video tag.
              </video>
            );
          })()}
      </Dialog>
    </div>
  );
};

export default CourseDetail;
